import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import { useSetBioMutation } from "Api/Mutations/Social/useSetBioMutation";
import { FormInput } from "Components/Shared/Inputs/Form/FormInput";
import { FieldEditForm } from "Components/UserAccount/Shared/PublicInformation/FieldEditForm";
import { useUserInfoQuery } from "Auth/Api/Queries/useUserInfoQuery";
import { Resources, type TFunction, useResource } from "Translations/Resources";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

export const BIO_MAX_LENGTH = 160;

const createBioSchema = (t: TFunction) =>
  z.object({
    bio: z
      .string()
      .trim()
      .max(BIO_MAX_LENGTH, t(Resources.Social.PublicPermissions.BioTooLong)),
  });

type BioFormValues = z.infer<ReturnType<typeof createBioSchema>>;

type Props = {
  onSuccess: () => void;
};

/**
 * Bio editor (bio card's mini dialog): free text shown on the public profile, max 160
 * characters, empty clears it. Saves via PUT api/social/bio; user-info refetch updates
 * the card.
 */
export const BioForm = ({ onSuccess }: Props) => {
  const { t } = useResource();
  const { data: userInfo } = useUserInfoQuery();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isDirty, errors },
  } = useForm<BioFormValues>({
    resolver: zodResolver(createBioSchema(t)),
    defaultValues: { bio: userInfo?.bio ?? "" },
  });

  const bio = watch("bio");

  const { mutate: setBio, isPending } = useSetBioMutation({
    onSuccess: () => {
      toast.success(t(Resources.Common.Saved));
      onSuccess();
    },
    onError: () =>
      toast.error(t(Resources.Social.PublicPermissions.BioSaveError)),
  });

  const onSubmit = (data: BioFormValues) => {
    setBio({ bio: data.bio || null });
  };

  return (
    <FieldEditForm
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isDirty}
      isSaving={isPending}
    >
      <FormInput
        control={control}
        errors={errors}
        name="bio"
        label={t(Resources.Social.PublicPermissions.BioLabel)}
        type="textarea"
        rows={3}
        maxLength={BIO_MAX_LENGTH}
        autoFocus
        helperText={
          <Box display="flex" justifyContent="flex-end" component="span">
            <Typography variant="body2" color="text.secondary" component="span">
              {`${bio.length} / ${BIO_MAX_LENGTH}`}
            </Typography>
          </Box>
        }
      />
    </FieldEditForm>
  );
};
